'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import QRCode from 'qrcode';

interface MemberQRCodeProps {
  memberId: string;
  /** Pixel size of the QR code. Defaults to 64. */
  size?: number;
}

/**
 * @component MemberQRCode
 * @description Generates and displays a QR code for a given member ID.
 * Shows a pulsing placeholder while the code is being generated.
 * @param memberId - The member ID to encode in the QR code.
 * @param size - Pixel width/height of the QR code. Defaults to 64.
 * @returns A Next.js Image element with the generated QR code, or a placeholder while loading.
 */
export default function MemberQRCode({ memberId, size = 64 }: MemberQRCodeProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  useEffect(() => {
    QRCode.toDataURL(memberId, {
      width: size,
      margin: 1,
      color: { dark: '#000000', light: '#FFFFFF' },
    }).then(setQrDataUrl).catch(console.error);
  }, [memberId, size]);

  if (!qrDataUrl) {
    return <div className="bg-used-badge animate-pulse" style={{ width: size, height: size }} />;
  }

  return <Image src={qrDataUrl} alt={`QR code for ${memberId}`} width={size} height={size} style={{ width: size, height: size }} unoptimized />;
}
